import { ArrowRight, MapPin } from "lucide-react";
import React, { useEffect, useState } from "react";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import { useNavigate } from "react-router-dom";
import { http } from "../../axios/axios";
import Pagination from "../Pagination";
import { Helmet } from "react-helmet-async";

const Blog = () => {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
  });
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetchSeo();
  }, []);

  const fetchSeo = async () => {
    try {
      const { data } = await http.get("/seo/Blog");
      setSeo(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const seoData = seo || {
    metaTitle: "",
    metaDescription:
      "",
    metaTags: [],
    schemaScript: null,
  };

  const schemaScript = seoData?.schemaScript
    ? typeof seoData.schemaScript === "string"
      ? seoData.schemaScript
      : JSON.stringify(seoData.schemaScript)
    : null;

  const fetchBlogs = async () => {
    try {
      const res = await http.get(
        `/blogs/published?page=${pagination.page}&limit=${pagination.limit}`,
      );

      if (res.data.success) {
        setBlogs(res.data.data);

        setPagination((prev) => ({
          ...prev,
          totalPages: res.data.totalPages,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [pagination.page]);
  return (
    <>
      <Helmet>
        <title>{seoData?.metaTitle}</title>

        <meta name="description" content={seoData?.metaDescription} />

        <meta
          name="keywords"
          content={
            Array.isArray(seoData?.metaTags)
              ? seoData?.metaTags.join(", ")
              : seoData?.metaTags
          }
        />

        {schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schemaScript }}
          />
        )}
      </Helmet>

      <div className="min-h-screen bg-white pb-20 font-sans">
        <header className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 py-20 px-6 text-center relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight font-serif">
              Our <span className="text-blue-400">Blogs</span>
            </h1>

            <p className="mt-6 text-lg text-gray-300 leading-relaxed">
              Stay updated with our latest insights, industry trends, expert
              tips, and informative articles covering security solutions, CCTV
              technology, access control systems, fire safety, surveillance
              innovations, and smart protection strategies for businesses,
              industries, and homes.
            </p>
          </div>
        </header>{" "}
        <section
          className="px-4 mt-6 md:px-6 md:mt-16"
          aria-labelledby="projects-grid-heading"
        >
          {" "}
          <h2 id="projects-grid-heading" className="sr-only">
            Security Projects Portfolio
          </h2>
           <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8"> */}
            {blogs.map((blog, index) => (
              <article
                key={blog._id}
                aria-labelledby={`blog-title-${blog._id}`}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/blogs/${blog.slug}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate(`/blogs/${blog.slug}`);
                  }
                }}
                className="group relative h-[280px] sm:h-[320px] lg:h-[360px] overflow-hidden rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                <figure className="relative h-full overflow-hidden">
                  <img
                    src={GetImageUrl(blog.blogBannerImage)}
                    loading={index < 6 ? "eager" : "lazy"}
                    fetchPriority={index < 3 ? "high" : "auto"}
                    decoding="async"
                    alt={blog.blogBannerImageAlt || blog.blogHeading}
                    className="absolute inset-0 h-full w-full object-cover brightness-90 transition-all duration-700 group-hover:scale-105"
                  />

                  <figcaption className="sr-only">
                    {blog.blogHeading}
                  </figcaption>
                </figure>

                <div className="absolute inset-0 bg-black/10" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />

                <div className="absolute left-5 top-5">
                  <span className="rounded-md bg-blue-600/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md">
                    {blog.blogCategory}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-7">
                  <p className="mb-2 text-sm text-gray-200">By {blog.author}</p>

                  <h3
                    id={`blog-title-${blog._id}`}
                    className="text-lg sm:text-xl 2xl:text-2xl font-bold text-white leading-tight line-clamp-2"
                  >
                    {blog.blogHeading}
                  </h3>

                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-400 transition-colors group-hover:text-white">
                    Read Blog
                    <ArrowRight
                      size={10}
                      className="transition-transform duration-300 group-hover:translate-x-2"
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
          {blogs.length > 0 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPrevious={() =>
                setPagination((prev) => ({
                  ...prev,
                  page: Math.max(prev.page - 1, 1),
                }))
              }
              onNext={() =>
                setPagination((prev) => ({
                  ...prev,
                  page: Math.min(prev.page + 1, prev.totalPages),
                }))
              }
            />
          )}
        </section>
      </div>
    </>
  );
};

export default Blog;
